import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatCurrency } from "@/lib/api";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { Loader2, RefreshCcw, TrendingUp, Calendar } from "lucide-react";

/**
 * Deposit renewal wizard.
 *
 * Fetches ``/investments/{id}/renewal-preview`` to compute the suggested
 * new principal (old principal + accrued interest) and new maturity
 * (same term as the original deposit). The user can override interest
 * rate, maturity date and name before confirming.
 */
export default function DepositRenewalDialog({
  open, onOpenChange, deposit, onRenewed,
}) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rate, setRate] = useState("");
  const [maturity, setMaturity] = useState("");
  const [name, setName] = useState("");

  const load = useCallback(async () => {
    if (!deposit?.id) return;
    setLoading(true);
    try {
      const r = await api.get(`/investments/${deposit.id}/renewal-preview`);
      setPreview(r.data);
      setRate(String(r.data.new_interest_rate ?? ""));
      setMaturity(r.data.new_maturity_date || "");
      setName(r.data.new_name || "");
    } catch (e) {
      logger.error("renewal preview", e);
      toast.error(e?.response?.data?.detail || "No se pudo calcular la renovación");
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  }, [deposit?.id, onOpenChange]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const confirm = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/investments/${deposit.id}/renew`, {
        new_interest_rate: rate ? Number(rate) : null,
        new_maturity_date: maturity || null,
        new_name: name || null,
      });
      toast.success("Depósito renovado ✨");
      onRenewed?.(r.data);
      onOpenChange(false);
    } catch (e) {
      logger.error("renew", e);
      toast.error(e?.response?.data?.detail || "No se pudo renovar");
    } finally {
      setSaving(false);
    }
  };

  const currency = preview?.currency || deposit?.currency || "EUR";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-white" data-testid="deposit-renew-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <RefreshCcw className="w-5 h-5 text-[#1C7C54]" />
            Renovar depósito
          </DialogTitle>
          <p className="text-sm text-[#5F7368]">
            {deposit?.name} · {formatCurrency(
              (Number(deposit?.quantity) || 0) * (Number(deposit?.purchase_price) || 0),
              currency,
            )} de principal actual
          </p>
        </DialogHeader>

        {loading || !preview ? (
          <div className="py-8 flex items-center justify-center text-[#5F7368]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Calculando…
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-lg border border-[#EBEBEA] px-3 py-2">
                <div className="text-[10px] uppercase tracking-[0.08em] font-bold text-[#8A9992]">Capital</div>
                <div className="font-display text-base font-semibold tabular-nums" data-testid="renew-principal">
                  {formatCurrency(preview.principal, currency)}
                </div>
              </div>
              <div className="rounded-lg border border-[#1C7C54]/25 bg-[#1C7C54]/5 px-3 py-2">
                <div className="text-[10px] uppercase tracking-[0.08em] font-bold text-[#1C7C54] flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> Intereses
                </div>
                <div className="font-display text-base font-semibold tabular-nums text-[#1C7C54]" data-testid="renew-interest">
                  +{formatCurrency(preview.interest_earned, currency)}
                </div>
              </div>
              <div className="rounded-lg border border-[#0F291E]/25 bg-[#0F291E]/5 px-3 py-2">
                <div className="text-[10px] uppercase tracking-[0.08em] font-bold text-[#0A110E]">Nuevo capital</div>
                <div className="font-display text-base font-semibold tabular-nums text-[#0A110E]" data-testid="renew-new-principal">
                  {formatCurrency(preview.new_principal, currency)}
                </div>
              </div>
            </div>

            <div className="space-y-3 pt-1">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">Nombre</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="renew-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">TIN %</Label>
                  <Input
                    type="number" step="0.01"
                    value={rate} onChange={(e) => setRate(e.target.value)}
                    data-testid="renew-rate"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992] flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Vencimiento
                  </Label>
                  <Input
                    type="date"
                    value={maturity} onChange={(e) => setMaturity(e.target.value)}
                    data-testid="renew-maturity"
                  />
                </div>
              </div>
            </div>

            <p className="text-[11px] text-[#8A9992] leading-relaxed">
              Se creará un nuevo depósito con el capital + intereses ya sumados y el depósito actual quedará marcado como vencido (podrás verlo en el historial).
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
          <Button
            onClick={confirm}
            disabled={loading || saving || !preview}
            className="bg-[#0F291E] hover:bg-[#1C7C54]"
            data-testid="renew-confirm-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
            Renovar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
